/**
 * sentry.client.ts — Browser Sentry React SDK init (Phase 3.6 Wave 0, D-03).
 *
 * Companion to handleError.ts. Called once from src/main.tsx BEFORE
 * createRoot(). Fail-soft per OWASP: dev mode skips, missing DSN skips,
 * handleError still logs + toasts even when init was a no-op.
 *
 * PII redaction (T-3.6-01): PII_KEYS_TO_REDACT exported for test parity.
 * Replay maskAllText:true; tracesSampleRate 0.1; ≤ 100 LOC per memory
 * `feedback_god_file_prevention`.
 *
 * @see .planning/phases/03.6-store-offer-cluster-refac/03.6-RESEARCH.md
 *      §"Pattern 5: handleError Utility (D-03)"
 * @see https://docs.sentry.io/platforms/javascript/guides/react/
 * @see https://cheatsheetseries.owasp.org/cheatsheets/Error_Handling_Cheat_Sheet.html
 */
import * as Sentry from '@sentry/react';

/**
 * PII keys redacted from every Sentry event (request.headers + breadcrumb.data).
 * Exported for test parity — same source-of-truth from both runtime and assertions.
 *
 * Note: comparison is case-sensitive — incoming headers are checked exactly as
 * keyed. Adding 'authorization' covers HTTP Bearer tokens; rare header casings
 * (e.g. 'Authorization') should also be handled if the source library uses
 * non-lowercased headers — current SDK normalizes to lowercase in browser fetch.
 */
export const PII_KEYS_TO_REDACT = [
  'password',
  'totp',
  'recoveryCode',
  'apiKey',
  'authorization',
] as const;

/**
 * Build the beforeSend hook that strips PII from each outbound event.
 * Extracted so tests can call it deterministically via the init mock.
 */
function buildBeforeSend() {
  return function beforeSend(event: any): any {
    if (event?.request?.headers && typeof event.request.headers === 'object') {
      for (const key of PII_KEYS_TO_REDACT) {
        if (key in event.request.headers) {
          event.request.headers[key] = '[REDACTED]';
        }
      }
    }
    if (Array.isArray(event?.breadcrumbs)) {
      event.breadcrumbs = event.breadcrumbs.map((bc: any) => {
        if (bc?.data && typeof bc.data === 'object') {
          for (const key of PII_KEYS_TO_REDACT) {
            if (key in bc.data) {
              bc.data[key] = '[REDACTED]';
            }
          }
        }
        return bc;
      });
    }
    return event;
  };
}

/**
 * Initialize Sentry React SDK. Idempotent at the framework level (Sentry's
 * own init() guard handles re-calls), so callers may invoke unconditionally.
 */
export function initSentry(): void {
  // Gate 1: skip outside production builds (Vite injects PROD as boolean true).
  // Behavior 9: when import.meta.env.PROD is falsy, return without init.
  if (!import.meta.env.PROD) {
    return;
  }
  // Gate 2: skip when DSN missing (fail-soft per OWASP).
  // Behavior 10: when VITE_SENTRY_DSN_CLIENT empty/undefined, return without init.
  const dsn = import.meta.env.VITE_SENTRY_DSN_CLIENT;
  if (!dsn) {
    return;
  }
  Sentry.init({
    dsn,
    environment: import.meta.env.MODE,
    release: import.meta.env.VITE_SENTRY_RELEASE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: true }),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    beforeSend: buildBeforeSend(),
  });
}
